<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useDocumentStore } from '@/modules/investing/stores/document-store'
import { RouterLinkBack, VBar, VButtonInvisible } from '@/modules/shared/components'
import EmbeddedSigningModal from '@/modules/signing/components/EmbeddedSigningModal.vue'
import DocumentThumbnail from '@/modules/signing/components/DocumentThumbnail.vue'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useRoute, useRouter } from 'vue-router'
import { Signing } from 'pandadoc-signing'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSigningFeatureFlags } from '@/modules/signing/composables/use-signing-feature-flags'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const documentStore = useDocumentStore()
const item_documents = ref([])
const notificationStore = useNotificationStore()
const hasSubscription = route.query.hasSubscription
const viewAsSigner = route.query.viewAsSigner === 'true'
const entityId = route.params.entity_id
const loading = ref(true)
const { isSpvAutoSigningEnabled } = useSigningFeatureFlags()

// Track recently completed documents for animation
const recentlyCompleted = ref(new Set())

// Fetch documents and filter awaiting
const fetchDocs = async () => {
  loading.value = true

  await documentStore.fetchDocuments('spv', entityId)

  item_documents.value = Array.from(documentStore.items.entries())
  loading.value = false

  // Fallback: if no documents and feature flag is disabled, skip to next step
  if (item_documents.value.length === 0 && !isSpvAutoSigningEnabled.value) {
    continueOnboarding()
  }
}

const continueOnboarding = () => {
  if (hasSubscription) {
    router.replace({ name: 'billing-entity', params: { billable_id: entityId, billable_type: 'spv' } })
    return
  }

  router.replace({
    name: 'investing.entity-overview',
    params: { entity_id: entityId, entity_type: 'spv' },
  })
}

onMounted(fetchDocs)

// PandaDoc signing
const signingInstance = ref(null)
const signingKey = computed(() => documentStore.signingSessionToken)
watch(signingKey, (token) => {
  if (!token) return
  ;(async () => {
    const mainContainer = document.getElementById('signing-main')
    if (!mainContainer) return
    const existing = document.getElementById('signing-container')
    if (existing) mainContainer.removeChild(existing)

    const container = document.createElement('div')
    container.id = 'signing-container'
    mainContainer.appendChild(container)

    signingInstance.value = new Signing(
      'signing-container',
      { width: '100%', height: '100%', sessionId: token },
      { region: 'com' },
    )
    await signingInstance.value.open()
  })()
})

// Close signing overlay
const closeSigning = async () => {
  if (!signingInstance.value) return
  signingInstance.value.destroy()
  documentStore.signingSessionToken = null
  notificationStore.enqueue(
    'success',
    'The signature has been submitted. Please allow a few minutes for the status to update.',
  )
  await fetchDocs()
}

// Check if user can sign a document
const canUserSign = (document) => {
  // Allow viewing as signer for demo/testing purposes
  if (viewAsSigner) {
    return document.signatures?.some((s) => s.status_code === 'awaiting_signature')
  }
  return document.signatures?.some(
    (s) => s.signer.id === authStore.current_user?.investor_id && s.status_code === 'awaiting_signature',
  )
}

// Group documents by state
const needsYourSignature = computed(() => item_documents.value.filter(([id, doc]) => canUserSign(doc)))

const waitingOnOthers = computed(() =>
  item_documents.value.filter(([id, doc]) => doc.status === 'awaiting' && !canUserSign(doc)),
)

const completedDocs = computed(() => item_documents.value.filter(([id, doc]) => doc.status === 'completed'))

// Progress tracking
const totalCount = computed(() => item_documents.value.length)
const signedCount = computed(() => completedDocs.value.length)
const progressPercent = computed(() =>
  totalCount.value > 0 ? Math.round((signedCount.value / totalCount.value) * 100) : 0,
)

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Get signature status text
const getSignatureDate = (sig) => {
  if (sig.status_code === 'awaiting_signature') {
    return 'awaiting signature'
  }
  return sig.signed_at ? `signed ${formatDate(sig.signed_at)}` : 'signed'
}

// Check if signature is current user (or first awaiting signer in demo mode)
const isCurrentUser = (sig, document) => {
  if (viewAsSigner && sig.status_code === 'awaiting_signature') {
    // In demo mode, treat the first awaiting signer as "You"
    const firstAwaiting = document?.signatures?.find((s) => s.status_code === 'awaiting_signature')
    return firstAwaiting?.id === sig.id
  }
  return sig.signer.id === authStore.current_user?.investor_id
}

// Get document state for rendering
const getDocumentState = (doc) => {
  if (doc.status === 'completed') return 'completed'
  if (canUserSign(doc)) return 'action'
  return 'waiting'
}
</script>

<template>
  <!-- Background gradients -->
  <div class="pointer-events-none fixed inset-0 z-0 bg-gradient-to-br from-white via-white to-gray-50"></div>

  <!-- Decorative background blobs -->
  <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden">
    <div
      class="from-[#52B7B9]/8 absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br to-[#3783B0]/5 blur-3xl"
    />
    <div
      class="from-[#52B7B9]/6 absolute -bottom-48 -left-48 h-[500px] w-[500px] rounded-full bg-gradient-to-tr to-transparent blur-3xl"
    />
  </div>

  <!-- Edge gradients -->
  <div class="pointer-events-none fixed bottom-0 left-0 right-0 z-40 h-12 bg-gradient-to-t from-white to-white/0"></div>
  <div class="pointer-events-none fixed left-0 right-0 top-0 z-40 h-12 bg-gradient-to-b from-white to-white/0"></div>

  <VBar />

  <div class="relative z-10 mx-auto mt-1 max-w-7xl space-y-8 py-8 pb-16">
    <!-- Back button -->
    <RouterLinkBack :fallback="{ name: 'investing.entities', params: { entity_type: 'spvs' } }">
      <VButtonInvisible>← Back</VButtonInvisible>
    </RouterLinkBack>

    <EmbeddedSigningModal embedTarget="signing-main" :showModal="signingKey !== null" @closeEditor="closeSigning" />

    <!-- Documents Section -->
    <div class="mx-auto max-w-3xl">
      <!-- Page Header -->
      <div class="mb-10 text-center">
        <!-- Title -->
        <h1 class="text-2xl font-semibold text-gray-900">Onboarding Documents</h1>

        <!-- Subtitle -->
        <p class="mt-2 text-sm text-gray-500">Review and sign the required documents to complete your SPV setup</p>
      </div>

      <!-- Progress Indicator -->
      <div v-if="!loading && totalCount > 0" class="mb-8 rounded-xl border border-gray-200 bg-white p-4">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">{{ signedCount }} of {{ totalCount }} documents signed</span>
          <span class="text-xs text-gray-400">{{ progressPercent }}%</span>
        </div>
        <div class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            class="progress-fill h-full rounded-full bg-gradient-to-r from-[#52B7B9] to-[#3783B0]"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="space-y-4">
        <div v-for="n in 2" :key="n" class="animate-pulse">
          <div class="flex gap-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
            <!-- Document thumbnail skeleton -->
            <div class="h-36 w-28 flex-shrink-0 rounded-lg bg-gray-100" />

            <!-- Content skeleton -->
            <div class="flex flex-1 flex-col justify-between py-1">
              <div class="space-y-3">
                <div class="flex items-center gap-3">
                  <div class="h-5 w-48 rounded bg-gray-100" />
                  <div class="h-5 w-24 rounded-full bg-gray-100" />
                </div>
                <div class="flex gap-3">
                  <div class="h-3 w-16 rounded bg-gray-100" />
                  <div class="h-3 w-20 rounded bg-gray-100" />
                </div>
              </div>
              <div class="h-10 w-32 rounded-lg bg-gray-100" />
            </div>
          </div>
        </div>
      </div>

      <!-- Document Grid -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="item_documents.length === 0" class="py-12 text-center">
          <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <svg class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 class="mt-4 text-lg font-medium text-gray-900">No documents to sign</h3>
          <p class="mt-1 text-sm text-gray-500">All documents have been processed or none are required.</p>
        </div>

        <!-- Document Cards -->
        <div v-else class="grid grid-cols-2 gap-4">
          <div v-for="[id, document] in item_documents" :key="id" class="group relative">
            <!-- Subtle glow effect (action only) -->
            <div
              v-if="getDocumentState(document) === 'action'"
              class="absolute -inset-2 rounded-2xl bg-gradient-to-r from-[#52B7B9]/15 to-[#3783B0]/15 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
            />

            <div
              class="document-card relative flex h-full flex-col items-center rounded-2xl border border-gray-200 px-6 pb-6 pt-10 text-center shadow-sm transition-all duration-300"
              :class="{
                'bg-gradient-to-b from-white to-gray-50/80 hover:border-[#52B7B9]/30 hover:shadow-lg':
                  getDocumentState(document) === 'action',
                'bg-gradient-to-b from-white to-gray-50/80': getDocumentState(document) === 'waiting',
                'bg-gradient-to-b from-white to-green-50/50': getDocumentState(document) === 'completed',
              }"
            >
              <!-- Status Badge -->
              <span
                class="absolute right-3 top-3 rounded-full px-2 py-0.5 text-xs font-medium"
                :class="{
                  'bg-[#52B7B9]/10 text-[#52B7B9]': getDocumentState(document) === 'action',
                  'bg-gray-100 text-gray-500': getDocumentState(document) === 'waiting',
                  'bg-green-100 text-green-700': getDocumentState(document) === 'completed',
                }"
              >
                {{
                  getDocumentState(document) === 'action'
                    ? 'Action Required'
                    : getDocumentState(document) === 'waiting'
                      ? 'Waiting'
                      : 'Completed'
                }}
              </span>

              <!-- Document Thumbnail -->
              <div class="mb-4">
                <DocumentThumbnail :variant="getDocumentState(document)" />
              </div>

              <!-- Document Title -->
              <h3
                class="line-clamp-2 font-semibold"
                :class="{
                  'text-gray-900': getDocumentState(document) === 'action',
                  'text-gray-600': getDocumentState(document) === 'waiting',
                  'text-gray-700': getDocumentState(document) === 'completed',
                }"
              >
                {{ document.name }}
              </h3>

              <!-- Completion Date (completed only) -->
              <p
                v-if="getDocumentState(document) === 'completed' && document.completed_at"
                class="mt-1 text-xs text-gray-500"
              >
                {{ formatDate(document.completed_at) }}
              </p>

              <!-- Signatures -->
              <div class="my-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
                <div v-for="sig in document.signatures" :key="sig.id" class="flex items-center gap-1.5">
                  <div
                    v-if="getDocumentState(document) === 'completed'"
                    class="h-1.5 w-1.5 rounded-full bg-green-500"
                  />
                  <div
                    v-else-if="sig.status_code === 'awaiting_signature'"
                    class="h-1.5 w-1.5 rounded-full"
                    :class="
                      isCurrentUser(sig, document) && getDocumentState(document) === 'action'
                        ? 'bg-[#52B7B9]'
                        : 'bg-gray-300'
                    "
                  />
                  <div v-else class="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span
                    class="text-xs"
                    :class="{
                      'font-medium text-[#52B7B9]':
                        isCurrentUser(sig, document) &&
                        getDocumentState(document) === 'action' &&
                        sig.status_code === 'awaiting_signature',
                      'text-gray-500':
                        !isCurrentUser(sig, document) ||
                        getDocumentState(document) !== 'action' ||
                        sig.status_code !== 'awaiting_signature',
                      'text-gray-600': getDocumentState(document) === 'completed',
                    }"
                  >
                    {{ isCurrentUser(sig, document) ? 'You' : sig.signer.name }}
                  </span>
                </div>
              </div>

              <!-- CTA Button (action only) -->
              <button
                v-if="getDocumentState(document) === 'action'"
                class="mt-auto w-full rounded-lg bg-[#3b88af] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md hover:brightness-105"
                @click="documentStore.createDocumentSigningSession(document.id)"
              >
                Review & Sign
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Continue Button -->
      <div class="mt-12 border-t border-gray-100 pt-8">
        <button
          class="w-full rounded-xl bg-gray-900 py-4 text-sm font-medium text-white transition-all duration-200 hover:bg-gray-800"
          @click="continueOnboarding"
        >
          Continue to {{ hasSubscription ? 'Billing' : 'SPV Overview' }}
        </button>

        <p class="mt-3 text-center text-xs text-gray-400">You can always return to sign remaining documents later</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Progress bar fill animation */
.progress-fill {
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Document card transition */
.document-card {
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
